import React from 'react';
import { Heart } from 'lucide-react';
import HeaderOne from '../common/header/HeaderOne';
import FooterOne from '../common/footer/FooterOne';
import Breadcrumb from '../common/breadcrumb/Breadcrumb';
import { FavoritesGrid } from '@/components/favorites';
import { useFavorites } from '@/hooks/use-favorites';
import { toast } from 'sonner';

const Favorites: React.FC = () => {
  const {
    totalCount,
    clearFavorites,
    exportFavorites,
    importFavorites,
  } = useFavorites();

  const handleExport = () => {
    const data = exportFavorites();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'my-favorites.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result as string;
      const result = importFavorites(data);
      if (result.success) {
        toast.success(`Imported ${result.added} favorite${result.added === 1 ? '' : 's'}`);
      } else {
        toast.error('Failed to import favorites. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <>
      <HeaderOne />
      <Breadcrumb
        title="My Favorites"
        currentPage="Favorites"
        img="/assets/images/backgrounds/breadcrumb-bg.jpg"
      />

      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-bark mb-2">
                My Favorites
              </h1>
              <p className="text-muted-foreground">
                {totalCount} saved item{totalCount !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleExport}
                disabled={totalCount === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium border border-input bg-white hover:bg-sage rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Export
              </button>
              <label className="inline-flex items-center px-4 py-2 text-sm font-medium border border-input bg-white hover:bg-sage rounded-lg cursor-pointer transition-colors">
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
              {totalCount > 0 && (
                <button
                  onClick={clearFavorites}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-destructive border border-destructive/30 bg-white hover:bg-destructive/10 rounded-lg transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {totalCount === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow-sm">
              <div className="max-w-md mx-auto">
                <div className="mb-4 flex justify-center">
                  <Heart className="h-16 w-16 text-red-400" />
                </div>
                <h2 className="text-xl font-semibold text-bark mb-2">
                  No Favorites Yet
                </h2>
                <p className="text-muted-foreground mb-6">
                  Save your favorite vendors, markets, and products to quickly
                  access them later. Click the heart icon on any listing.
                </p>
                <a
                  href="/browse"
                  className="inline-flex items-center justify-center px-6 py-3 bg-forest text-white font-medium rounded-lg hover:bg-forest-light transition-colors"
                >
                  Explore Directory
                </a>
              </div>
            </div>
          ) : (
            <FavoritesGrid />
          )}
        </div>
      </section>

      <FooterOne />
    </>
  );
};

export default Favorites;
